import {
  ListItemIcon,
  ListItemText,
  MenuItem,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useHistory, useLocation } from "react-router-dom";

import SidebarActiveLinkIcon from "assets/icons/sidebar-active-link.svg";

export interface SidebarMenuItemsProps {
  icon: JSX.Element;
  text: string | JSX.Element;
  link?: string;
  onClick?: () => void;
  collapsed?: boolean;
}

const SidebarMenuItem = ({
  icon,
  text,
  link,
  onClick,
  collapsed,
}: SidebarMenuItemsProps) => {
  const history = useHistory();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const { pathname } = useLocation();
  const isClickable = Boolean(link || onClick);
  const isSelected = Boolean(link && pathname.includes(link));

  const handleClick = () => {
    if (link) history.push(link);
    onClick?.();
  };

  const menuItem = (
    <MenuItem
      onClick={handleClick}
      disableRipple
      selected={isSelected}
      sx={{
        py: 1.5,
        mb: 0.5,
        borderRadius: 2,
        pointerEvents: isClickable ? "auto" : "none",
        position: "relative",
        ...(isDesktop
          ? collapsed
            ? {
                px: 0,
                ml: 1,
                pr: 1,
                // justifyContent: "center",
                minHeight: 0,
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
              }
            : {
                pl: 0,
                mx: 1,
                mr: 0,
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
              }
          : { mx: 1.5 }),
        "&.Mui-selected": {
          bgcolor: (t: any) => t.palette.brandBackground?.primary ?? "#FAF9F6",
          "&:hover": {
            bgcolor: (t: any) =>
              t.palette.brandBackground?.primary ?? "#FAF9F6",
          },
          ...(isDesktop && {
            "&::after": {
              content: '""',
              position: "absolute",
              width: 11,
              height: 48 + 11 * 2,
              right: 0,
              top: -11,
              background: `url(${SidebarActiveLinkIcon}) no-repeat center center / contain`,
              pointerEvents: "none",
            },
          }),
        },
      }}
    >
      <ListItemIcon
        sx={{
          width: 53,
          minWidth: 53,
          justifyContent: "center",
          color: isSelected
            ? (t: any) => t.palette.brand?.primary ?? "#264B9F"
            : "#ABB3B2",
          "& svg": { fontSize: 22 },
        }}
      >
        {icon}
      </ListItemIcon>
      {!collapsed && (
        <ListItemText
          primary={text}
          sx={{
            "& .MuiListItemText-primary": {
              fontSize: 13,
              fontWeight: 600,
              color: isSelected
                ? (t: any) => t.palette.brand?.primary ?? "#264B9F"
                : "#ABB3B2",
            },
          }}
        />
      )}
    </MenuItem>
  );

  if (collapsed && typeof text === "string") {
    return (
      <Tooltip title={text} placement="right" arrow>
        {menuItem}
      </Tooltip>
    );
  }

  return menuItem;
};

export default SidebarMenuItem;
